import { Component, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { createNewUser } from '../../../admin/store/admin.action';
import { RoleFullDto } from '../../../authentication/model/role.model';
import { UserCreation } from '../../../admin/model/user-creation.model';
import { selectAllRoles } from '../../../admin/store/admin.selector';
import { getContextCode, isRoleSelected, toggleRole, getLabelForRole } from '../user-tools';
import { ContextValues } from '../../../admin/model/context-values.model';
import { map, shareReplay, take } from 'rxjs/operators';
import { UserAdminService } from '../../../admin/service/user-admin.service';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { PrimaryButtonComponent } from '../../../shared/buttons/primary-button.component';
import { MatChipListbox, MatChipOption, MatChipAvatar } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'sibat-new-user',
  templateUrl: 'new-user.component.html',
  styleUrls: ['new-user.component.scss'],
  imports: [
    TranslocoDirective,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSelect,
    MatOption,
    PrimaryButtonComponent,
    MatChipListbox,
    MatChipOption,
    MatIcon,
    MatChipAvatar,
    TranslocoPipe,
    AsyncPipe,
  ],
})
export class NewUserComponent implements OnInit {
  userForm: UntypedFormGroup;
  roles$: Observable<RoleFullDto[]>;
  contextValuesCache$: { [key: string]: Observable<ContextValues[]> } = {};
  roles: UntypedFormControl;
  selectedRole: string;

  constructor(private store: Store, private formBuilder: UntypedFormBuilder, private userAdminService: UserAdminService) {}

  get roleContextValues(): UntypedFormArray {
    return this.userForm.get('roleContextValues') as UntypedFormArray;
  }

  ngOnInit(): void {
    this.selectedRole = '';
    this.roles$ = this.store.select(selectAllRoles);
    this.userForm = this.formBuilder.group({
      lastName: ['', { validators: [Validators.required], updateOn: 'change' }],
      firstName: ['', { validators: [Validators.required], updateOn: 'change' }],
      email: [
        '',
        {
          validators: [Validators.required, Validators.email],
          updateOn: 'change',
        },
      ],
      phoneNumber: ['', { updateOn: 'change' }],
      roles: [[], { validators: [Validators.required], updateOn: 'change' }],
      language: ['', { validators: [Validators.required], updateOn: 'change' }],
      municipalities: ['', { updateOn: 'change' }],
      roleContextValues: this.formBuilder.array([]),
    });
  }

  selectedValue() {
    this.selectedRole = this.userForm.get('role')?.value;
  }

  getContextValuesForSelectedRole(roleCode: string): Observable<ContextValues[]> {
    if (!this.contextValuesCache$[roleCode]) {
      if (!this.isRoleLoaded(roleCode)) {
        return of([]);
      }

      const contextCode = this.getContextCodeForRole(roleCode);
      if (!contextCode) {
        this.contextValuesCache$[roleCode] = of([]);
      } else {
        this.contextValuesCache$[roleCode] = this.userAdminService.fetchContextValues(roleCode).pipe(
          map(contextValues => contextValues.sort((a, b) => a.geoFieldAlias.localeCompare(b.geoFieldAlias))),
          shareReplay(1)
        );
      }
    }

    return this.contextValuesCache$[roleCode];
  }

  getContextCodeForRole(selectedRole: string): string | null {
    let contextLabel: string | null = null;
    this.roles$.pipe(take(1)).subscribe(roles => {
      contextLabel = getContextCode(roles, selectedRole);
    });
    return contextLabel;
  }

  isRoleSelected(selectedRole: string): boolean {
    return isRoleSelected(this.userForm, selectedRole);
  }

  getLabelForRole(selectedRole: { name: string; code: string }): string | null {
    return selectedRole.name;
  }

  toggleRole(changedRole: string) {
    if (changedRole === 'role_api') {
      return;
    }
    toggleRole(this.userForm, changedRole, this.roles$);
    this.initRoleContexts(this.userForm.get('roles')?.value);
  }

  onSubmit() {
    const newUser: UserCreation = {
      firstName: this.userForm.get('firstName')?.value,
      lastName: this.userForm.get('lastName')?.value,
      email: this.userForm.get('email')?.value,
      phoneNumber: this.userForm.get('phoneNumber')?.value,
      language: this.userForm.get('language')?.value,
      roles: this.userForm.get('roles')?.value,
      roleContextValues: this.cleanUpRoleContextValues(),
    };
    this.store.dispatch(createNewUser({ newUser }));
  }

  private isRoleLoaded(selectedRole: string): boolean {
    let loaded = false;
    this.roles$.pipe(take(1)).subscribe(roles => {
      loaded = roles.some(role => role.code === selectedRole);
    });
    return loaded;
  }

  private initRoleContexts(roles: string[] | undefined): void {
    const contextValuesArray = this.userForm.get('roleContextValues') as UntypedFormArray;
    contextValuesArray.clear();

    if (!roles) {
      return;
    }

    roles.forEach(r => {
      const contextValueControl = this.formBuilder.group({
        roleCode: r,
        contextValues: this.formBuilder.control([], Validators.required),
      });
      contextValuesArray.push(contextValueControl);
    });
  }

  private cleanUpRoleContextValues() {
    const selectedRoles = this.userForm.get('roles')?.value || [];
    return this.roleContextValues.value
      .filter(
        ({ roleCode, contextValues }) => selectedRoles.some(selectedRole => selectedRole.code === roleCode.code) && contextValues.length > 0
      )
      .map(({ roleCode, contextValues }) => ({
        roleCode: roleCode.code,
        contextValues,
      }));
  }
}
